<template>
  <div v-if="!assignedCoach?.fullName">
    <iLoaderSimple :loading-animation-duration-ms="3000">
      <template #title>Matching you with the perfect coach</template>
      <template #subtitle>This will only take a few seconds...</template>
    </iLoaderSimple>
  </div>
  <div class="i-select-coach">
    <iBackground
      :background-image="bgGradient"
      image-position="top center"
    />
    <iReportNavbar
      :core-items="navigationItemsData.coreItems"
      :profile-items="navigationItemsData.profileItems"
      :support-items="navigationItemsData.supportItems"
      :style="{ zIndex: isDesktopOrLarger && data.showCalcomEmbedModal ? '0' : '600' }"
    />

    <div
      v-if="!isDesktopOrLarger && data.activeCoachId"
      class="back-button"
      @click="unsetActiveCoach()"
    >
      <img :src="iconArrowLeftGray" />
    </div>

    <div class="header">
      <div class="title">Select Your Coach</div>
      <div class="sub">
        <template v-if="userStore.isTierFinished">
          Your
          <span class="badge"><span class="inner">Premium Report</span></span>
          includes a 30-min 1:1 coaching session with an expert career coach.
        </template>
        <template v-else>
          Based on your input, you have been matched with {{ assignedCoach?.fullName }} at a
          <strong>{{ assignedCoach?.percentMatch }}% match score</strong>.
        </template>
      </div>
    </div>

    <div class="main">
      <iSelectedCoachDetails
        v-if="!isDesktopOrLarger && activeCoach"
        :coach="activeCoach"
        :is-assigned-coach="data.activeCoachId === assignedCoach.id"
        :active-tab="data.activeTab"
        :show-calcom-embed-modal="data.showCalcomEmbedModal"
        @set-active-tab="data.activeTab = $event"
        @set-next-coach-active="setNextCoachActive"
        @open-calcom-modal="data.showCalcomEmbedModal = $event"
      />
      <section class="section-coaches-list">
        <div class="container">
          <iCoachProfileCard
            :coach="assignedCoach"
            :active-coach-id="data.activeCoachId || 0"
            is-assigned-coach
            @set-active-coach="data.activeCoachId = $event"
            @set-active-tab="data.activeTab = $event"
            @open-calcom-modal="data.showCalcomEmbedModal = $event"
          />
          <template
            v-for="coach in allCoachesExceptAssigned"
            :key="coach.fullName"
          >
            <iCoachProfileCard
              :coach="coach"
              :active-coach-id="data.activeCoachId || 0"
              @set-active-coach="data.activeCoachId = $event"
              @set-active-tab="data.activeTab = $event"
              @open-calcom-modal="data.showCalcomEmbedModal = $event"
            />
          </template>
        </div>
      </section>
      <iSelectedCoachDetails
        v-if="isDesktopOrLarger && activeCoach"
        :coach="activeCoach"
        :is-assigned-coach="data.activeCoachId === assignedCoach.id"
        :active-tab="data.activeTab"
        :show-calcom-embed-modal="data.showCalcomEmbedModal"
        @set-active-tab="data.activeTab = $event"
        @set-next-coach-active="setNextCoachActive"
        @open-calcom-modal="data.showCalcomEmbedModal = $event"
      />
    </div>

    <iBaseModal
      visible
      class="zigpoll-modal"
      :class="{ visible: data.zigpollModalVisible }"
      @click-on-close="data.zigpollModalVisible = false"
    >
      <div class="zigpoll-modal-wrap">
        <div class="zigpoll-modal-text">You can close this modal now!</div>
        <div id="zigpoll-embed-target"></div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useCoachingStore } from "@/stores/coaching"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { breakpointLg } from "@/styles/variables"

import onAwayChangeDocumentTitle from "@/composables/onAwayChangeDocumentTitle"
import useIsScreenSize from "@/composables/useIsScreenSize"
import navigationItems from "@/composables/navigation"
import { GlobalVariables } from "@/helpers/globalVariables"
import GtmService from "@/services/gtmService"

// Components
import iBaseModal from "@/components/base/BaseModal.vue"
import iCoachProfileCard from "@/components/coaching/selectCoach/CoachProfileCard.vue"
import iSelectedCoachDetails from "@/components/coaching/selectCoach/SelectedCoachDetails.vue"
import iLoaderSimple from "@/components/global/LoaderSimple.vue"
import iReportNavbar from "@/components/report/navbar/ReportNavbar.vue"
import iBackground from "@/components/global/Background.vue"

// ASSETS
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import bgGradient from "@/assets/confirm/bg-gradient-multi.png"

// STORES AND ROUTES
const coachingStore = useCoachingStore()
const reportStore = useReportStore()
const userStore = useUserStore()
const activeRoute = useRoute()
const router = useRouter()
const defaultTab = activeRoute.query["tab"]?.toString()

// COMPOSABLES
const isDesktopOrLarger = useIsScreenSize(breakpointLg)

// DATA
const data = reactive({
  zigpollModalVisible: false,
  zigpollModalDelayMs: 45000, // 45 seconds
  activeCoachId: null as number | null,
  activeTab: 1 as 1 | 2 | 3,
  showCalcomEmbedModal: false
})

// COMPUTED
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const allCoachesExceptAssigned = computed(() =>
  coachingStore.getCoachesForSelectionPage.filter((e) => e.id !== assignedCoach.value.id)
)
const activeCoach = computed(() => {
  return data.activeCoachId === assignedCoach.value.id
    ? assignedCoach.value
    : allCoachesExceptAssigned.value.find((e) => e.id === data.activeCoachId)
})
const navigationItemsData = computed(() => navigationItems())

// FUNCTIONS
const unsetActiveCoach = () => (data.activeCoachId = null)

const setNextCoachActive = () => {
  const allCoaches = [assignedCoach.value, ...allCoachesExceptAssigned.value]
  const activeCoachIndex = allCoaches.findIndex((e) => e.id === data.activeCoachId)
  const nextCoachIndex = activeCoachIndex + 1 != allCoaches.length ? activeCoachIndex + 1 : 0
  data.activeCoachId = allCoaches[nextCoachIndex].id
}

const loadZigpoll = (delayMs: number) => {
  const isMobileView = window.innerWidth <= breakpointLg

  if (isMobileView) {
    reportStore.initMobileZigpollPrepayment(delayMs, GlobalVariables.zigpolls.finishedCoaching).then((modalVisible) => {
      data.zigpollModalVisible = modalVisible
    })
  } else {
    reportStore.initDesktopZigpollPrepayment(delayMs)
  }
}

/**
 * Sends a GTM event when the user views the select coach page
 */
const sendGtmEvent = async () => {
  await GtmService.viewSelectCoachEvent()
}

onAwayChangeDocumentTitle()

// LIFECYCLE HOOKS
onMounted(async () => {
  await userStore.loadUserData()

  sendGtmEvent()
  loadZigpoll(data.zigpollModalDelayMs)

  if (!coachingStore.hasCoaches) await coachingStore.loadCoaches()
  if (!coachingStore.hasAssignedCoach) await coachingStore.loadAssignedCoach()

  if (isDesktopOrLarger.value) {
    data.activeCoachId = coachingStore.getAssignedCoach.id
  }

  if (defaultTab && [1, 2, 3].includes(parseInt(defaultTab))) {
    data.activeTab = parseInt(defaultTab) as 1 | 2 | 3
  } else data.activeTab = coachingStore.getAssignedCoach.matchReason ? 1 : 2

  router.replace({ query: undefined })
})
</script>

<style lang="scss" scoped>
.i-select-coach {
  position: relative;

  .back-button {
    @include border-radius-rounded;
    @include flex(column, center, center);

    position: absolute;
    height: 40px;
    width: 40px;
    left: 25px;
    top: 8px;
    border: 1px solid $zumthor100;

    @media screen and (min-width: $breakpoint-md) {
      left: 55px;
      top: 17px;
    }
  }

  .header {
    @include flex(column, center, start, $gap: 8px);

    margin: auto;
    padding: 15px 25px 10px;
    text-align: left;

    @media screen and (min-width: $breakpoint-md) {
      padding: 30px 55px 10px;
      align-items: center;
      text-align: center;
    }

    .title {
      @include display-xs;

      font-weight: 700;
      color: $black950;

      @media screen and (min-width: $breakpoint-md) {
        @include display-md;

        font-size: 40px;
      }
    }

    .sub {
      @include text-sm;

      line-height: 22px;
      font-weight: 500;
      color: $black700;

      @media screen and (min-width: $breakpoint-md) {
        @include text-xl;
      }

      .badge {
        @include border-radius-xxl;

        width: fit-content;
        background-image: $blue-purple-gradient;
        margin: 0 3px;

        .inner {
          @include border-radius-xxl;

          padding: 0 12px;
          background-color: #ffffffda;
          mix-blend-mode: screen;
        }
      }
    }
  }

  .main {
    @include flex(column, start, stretch, $gap: 15px);

    position: sticky;
    top: 0;
    padding: 10px;

    @media screen and (min-width: $breakpoint-md) {
      padding: 25px;
    }

    @media screen and (min-width: $breakpoint-lg) {
      @include grid($cols: minmax(400px, 450px) 1fr, $rows: 100%, $align-items: stretch, $gap: 15px);

      height: fit-content;
      max-height: 100vh;
    }

    @media screen and (min-width: $breakpoint-xl) {
      grid-template-columns: minmax(450px, auto) 1fr;
    }

    > div,
    > section {
      @media screen and (min-width: $breakpoint-lg) {
        overflow: scroll;
        -ms-overflow-style: none;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    .section-coaches-list {
      border-radius: 20px;
      padding: 15px;

      @media screen and (min-width: $breakpoint-md) {
        padding: 0 30px;
      }

      .container {
        @include flex(column, start, center, $gap: 20px);

        max-width: 1280px;
        margin: auto;
        width: 100%;
      }
    }
  }

  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    &-wrap {
      display: flex;
      flex-direction: column;
      min-height: 90px;
      max-height: 60vh;
      overflow: auto;
      position: relative;
    }

    &-text {
      font-weight: bold;
      position: absolute;
      text-align: center;
      top: 50%;
      width: 100%;
    }
  }
}
</style>
